import {
  ShipmentCreationFlowEnum,
  ShipmentDirectionEnum,
} from '@core/models/shipment-type';
import { ShipOrQuoteState } from '@core/state/ship-or-quote/interfaces/shipment.interface';
import {
  ClearInternationalStep,
  ClearOptionsStep,
  ClearShipOrQuote,
  SetAdditionalDocuments,
  SetAvailableLabelTypes,
  SetCommercialInvoice,
  SetCommodityAndInvoiceRequired,
  SetCommodityValid,
  SetDefaultLabelType,
  SetEstimatedDeliveryLoading,
  SetEstimatedDeliveryMinShipDate,
  SetEstimatedDeliveryShipDate,
  SetInsurance,
  SetInternational,
  SetInternationalCommodityList,
  SetInternationalDocumentOnlyShipment,
  SetInternationalDutiesAndTaxes,
  SetLabelType,
  SetNotificationSettings,
  SetOptionsLabelSize,
  SetOptionsReturnLabel,
  SetOptionsShipmentIdentifiers,
  SetPackages,
  SetPackageType,
  SetPickupDetail,
  SetPickupEligibility,
  SetPickupEligibilityList,
  SetPickupRequestInfo,
  SetPickupValidity,
  SetPreferredCarrier,
  SetReturnInsurance,
  SetServiceAndPackageAvailabilityLoading,
  SetServiceOptions,
  SetServiceType,
  SetShipment,
  SetShipmentBilling,
  SetShipmentBillingValidity,
  SetShipmentBroker,
  SetShipmentContents,
  SetShipmentContentsEditAction,
  SetShipmentDangerousGoods,
  SetShipmentDestination,
  SetShipmentDirectionCalculation,
  SetShipmentEEI,
  SetShipmentEEIShowComponent,
  SetShipmentIsOriginDestinationValid,
  SetShipmentOrigin,
  SetShipmentServiceOptions,
  SetShipmentServiceOptionsValidity,
  SetSpecialInstructions,
} from '@core/state/ship-or-quote/ship-or-quote.actions';
import {
  advanceInsuranceDerivedValue,
  EMPTY_INSURANCE_DERIVED_VALUE,
} from '@core/utils/insurance.utils';
import { createReducer, on } from '@ngrx/store';
import { IShipmentCommodityModel } from 'src/app/modules/customer/features/commodity/models/commodity.model';
import { PickupEligibilityEnum } from 'src/app/modules/shipping/features/pickup/models/pickup';
import { DangerousGoodsEnum } from 'src/app/modules/shipping/features/shipOrQuote/models/dangerousGoodsEnum';
import { BrokerTypeEnum } from '../../../modules/shipping/features/shipOrQuote/models/shipment/broker';

export const initialShipOrQuoteState: ShipOrQuoteState = {
  shipment: {
    origin: { valid: false, value: {} },
    destination: { valid: false, value: {} },
    isOriginDestinationValid: { valid: false },
    billing: { valid: false, value: {} as any },
    contents: { packages: [], valid: false },
    serviceOptions: {},
    dangerousGoods: {
      value: [DangerousGoodsEnum.NO],
      valid: true,
    },
    broker: {
      value: { type: BrokerTypeEnum.FEDEX },
      valid: true,
    },
  },
  options: {
    insurance: { value: {}, valid: true },
    returnInsurance: { value: {}, valid: true },
    insuranceMaxValue: EMPTY_INSURANCE_DERIVED_VALUE,
    insuranceAutoDescription: EMPTY_INSURANCE_DERIVED_VALUE,
    returnLabel: { valid: true },
    shipmentIdentifiers: { valid: false },
    labelSize: { valid: true, value: {} },
    pickup: {
      valid: false,
      pickupRequestInfo: {
        explanation: '',
        willDropOffPackagesAtFedex: false,
      },
      eligibility: PickupEligibilityEnum.NONE,
    },
    specialInstructions: { valid: true, value: '' },
    notificationSettings: { valid: true, value: [] },
    additionalDocuments: {
      documents: [],
      valid: true,
    },
  },
  international: {
    dutiesAndTaxes: {
      valid: false,
    },
    whatAreYouShipping: {
      documentOnlyShipment: false,
      valid: true,
    },
    commodities: {
      commodityList: [],
      isRequired: true,
      valid: false,
    },
    commercialInvoice: {
      value: {},
      isUploadDocument: false,
      isRequired: true,
      valid: false,
    },
    eei: {
      show: false,
      isRequired: true,
      value: { require: null },
      valid: false,
    },
  },
  directionCalculation: {
    valid: false,
    shipmentDirection: ShipmentDirectionEnum.WITHIN_US,
    deliveryCountryGroup: '',
    international: false,
  },
  labelType: '',
  defaultLabelType: '',
  availableLabelTypes: [],
  preferredCarrier: null,
  packageType: null,
  estimatedDelivery: {
    shipDate: null,
    minShipDate: null,
    loading: false,
  },
  packages: [],
  serviceOptions: [],
  serviceAndPackageAvailabilityLoading: true,
  serviceType: ShipmentCreationFlowEnum.CLASSIC_PARCEL,
};

export const shipOrQuoteReducer = createReducer(
  initialShipOrQuoteState,
  on(SetShipment, (state, { shipment }) => {
    return {
      ...state,
      shipment,
    };
  }),
  on(SetShipmentOrigin, (state, { valid, value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        origin: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetShipmentDestination, (state, { valid, value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        destination: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetShipmentIsOriginDestinationValid, (state, { valid }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        isOriginDestinationValid: {
          valid,
        },
      },
    };
  }),
  on(SetShipmentBilling, (state, { value }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        billing: {
          value,
          valid: value?.siteId ? true : false,
        },
      },
    };
  }),
  on(SetShipmentBillingValidity, (state, { valid }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        billing: {
          ...state.shipment.billing,
          valid,
        },
      },
    };
  }),
  on(SetShipmentContents, (state, { contents }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents,
      },
    };
  }),
  on(SetShipmentContentsEditAction, (state, { actionType }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents: {
          ...state.shipment.contents,
          type: actionType,
        },
      },
    };
  }),
  on(SetShipmentServiceOptions, (state, { serviceOptions }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        serviceOptions,
      },
    };
  }),
  on(SetShipmentServiceOptionsValidity, (state, { valid }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        serviceOptions: {
          ...state.shipment.serviceOptions,
          valid,
        },
      },
    };
  }),
  on(SetShipmentDangerousGoods, (state, { dangerousGoods }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        dangerousGoods,
      },
    };
  }),
  on(SetShipmentBroker, (state, { broker }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        broker,
      },
    };
  }),
  on(SetPreferredCarrier, (state, { preferredCarrier }) => {
    return {
      ...state,
      preferredCarrier,
    };
  }),
  on(SetInsurance, (state, { insurance }) => ({
    ...state,
    options: {
      ...state.options,
      insurance: insurance ?? state.options.insurance,
    },
  })),

  on(SetReturnInsurance, (state, { returnInsurance }) => ({
    ...state,
    options: {
      ...state.options,
      returnInsurance: returnInsurance ?? state.options.returnInsurance,
    },
  })),
  on(SetOptionsReturnLabel, (state, { returnLabel }) => {
    return {
      ...state,
      options: {
        ...state.options,
        returnLabel,
      },
    };
  }),
  on(SetOptionsShipmentIdentifiers, (state, { shipmentIdentifiers }) => {
    return {
      ...state,
      options: {
        ...state.options,
        shipmentIdentifiers,
      },
    };
  }),
  on(SetOptionsLabelSize, (state, { labelSize }) => {
    return {
      ...state,
      options: {
        ...state.options,
        labelSize,
      },
    };
  }),
  on(SetSpecialInstructions, (state, { specialInstructions }) => {
    return {
      ...state,
      options: {
        ...state.options,
        specialInstructions,
      },
    };
  }),
  on(SetNotificationSettings, (state, { value, valid }) => {
    return {
      ...state,
      options: {
        ...state.options,
        notificationSettings: {
          value,
          valid,
        },
      },
    };
  }),
  on(SetInternational, (state, { international }) => {
    return {
      ...state,
      international,
    };
  }),
  on(SetInternationalDutiesAndTaxes, (state, { dutiesAndTaxes }) => {
    return {
      ...state,
      international: {
        ...state.international,
        dutiesAndTaxes,
      },
    };
  }),
  on(
    SetInternationalDocumentOnlyShipment,
    (state, { documentOnlyShipment }) => {
      return {
        ...state,
        international: {
          ...state.international,
          whatAreYouShipping: {
            ...state.international.whatAreYouShipping,
            documentOnlyShipment: documentOnlyShipment,
          },
        },
      };
    }
  ),
  on(SetInternationalCommodityList, (state, { commodityList }) => {
    const commodityInsuranceValue = commodityList
      .map((item: IShipmentCommodityModel) => Number(item?.totalValue) || 0)
      .reduce((accumulator: number, currentValue: number) => {
        return accumulator + currentValue;
      }, 0);

    const commodityInsuranceDescription = commodityList
      .map((item: IShipmentCommodityModel) => item?.description?.trim())
      .filter(
        (description: string | undefined): description is string =>
          !!description && description.length > 0
      )
      .map(
        (description: string) =>
          description.charAt(0).toUpperCase() + description.slice(1)
      )
      .join('. ');

    return {
      ...state,
      options: {
        ...state.options,
        insuranceMaxValue: advanceInsuranceDerivedValue(
          state.options.insuranceMaxValue,
          commodityInsuranceValue || null
        ),
        insuranceAutoDescription: advanceInsuranceDerivedValue(
          state.options.insuranceAutoDescription,
          commodityInsuranceDescription || null
        ),
      },
      international: {
        ...state.international,
        commodities: {
          ...state.international.commodities,
          commodityList,
        },
      },
    };
  }),
  // on(AddNewInternationalCommodityItem, (state, { commodityList }) => ({
  //   ...state,
  //   international: {
  //     ...state.international,
  //     commodities: { ...state.international.commodities, commodityList },
  //   },
  // })),
  // on(RemoveInternationalCommodityItem, (state, { selectedCommodity }) => ({
  //   ...state,
  //   international: {
  //     ...state.international,
  //     commodities: {
  //       ...state.international.commodities,
  //       commodityList: state.international.commodities.commodityList.filter(
  //         (item) => item !== selectedCommodity
  //       ),
  //     },
  //   },
  // })),
  on(SetShipmentDirectionCalculation, (state, { directionCalculation }) => {
    return {
      ...state,
      shipment: {
        ...state.shipment,
        contents: {
          ...state.shipment.contents,
        },
      },
      directionCalculation: {
        ...directionCalculation,
        // DirectionCalculation's first element is 'valid' and it is always there, so we should check length as higher than 1
        valid: Object.keys(directionCalculation).length > 1,
      },
    };
  }),
  on(SetLabelType, (state, { labelType }) => {
    return {
      ...state,
      labelType,
    };
  }),
  on(SetDefaultLabelType, (state, { defaultLabelType }) => {
    return {
      ...state,
      defaultLabelType,
    };
  }),
  on(SetAvailableLabelTypes, (state, { availableLabelTypes }) => {
    return {
      ...state,
      availableLabelTypes,
    };
  }),
  on(SetEstimatedDeliveryShipDate, (state, { shipDate }) => {
    return {
      ...state,
      estimatedDelivery: {
        ...state.estimatedDelivery,
        shipDate,
      },
    };
  }),
  on(SetEstimatedDeliveryMinShipDate, (state, { minShipDate }) => {
    return {
      ...state,
      estimatedDelivery: {
        ...state.estimatedDelivery,
        minShipDate,
      },
    };
  }),
  on(SetEstimatedDeliveryLoading, (state, { loading }) => {
    return {
      ...state,
      estimatedDelivery: {
        ...state.estimatedDelivery,
        loading,
      },
    };
  }),

  on(SetPackageType, (state, { packageType }) => {
    return {
      ...state,
      packageType,
    };
  }),
  on(SetCommodityAndInvoiceRequired, (state, { isRequired }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commodities: {
          ...state.international.commodities,
          isRequired,
        },
        commercialInvoice: {
          ...state.international.commercialInvoice,
          isRequired,
        },
      },
    };
  }),
  on(SetCommodityValid, (state, { isCommodityValid }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commodities: {
          ...state.international.commodities,
          valid: isCommodityValid.valid,
        },
      },
    };
  }),

  on(SetPickupEligibilityList, (state, { eligibilityList }) => {
    return {
      ...state,
      options: {
        ...state.options,
        pickup: {
          ...state.options.pickup,
          eligibilityList,
        },
      },
    };
  }),

  on(SetPickupEligibility, (state, { eligibility }) => {
    return {
      ...state,
      options: {
        ...state.options,
        pickup: {
          ...state.options.pickup,
          eligibility,
        },
      },
    };
  }),
  on(SetPickupRequestInfo, (state, { pickupRequestInfo }) => {
    return {
      ...state,
      options: {
        ...state.options,
        pickup: {
          ...state.options.pickup,
          pickupRequestInfo,
        },
      },
    };
  }),
  on(SetPickupValidity, (state, { valid }) => {
    return {
      ...state,
      options: {
        ...state.options,
        pickup: {
          ...state.options.pickup,
          valid,
        },
      },
    };
  }),
  on(
    SetPickupDetail,
    (
      state,
      {
        valid,
        pickupRequestInfo,
        pickupType,
        pickupEligibility,
        pickupEligibilityList,
      }
    ) => {
      return {
        ...state,
        options: {
          ...state.options,
          pickup: {
            pickupRequestInfo: pickupRequestInfo
              ? pickupRequestInfo
              : state.options.pickup.pickupRequestInfo,
            pickupType,
            valid,
            eligibility: pickupEligibility,
            eligibilityList: pickupEligibilityList,
          },
        },
      };
    }
  ),
  on(SetCommercialInvoice, (state, { value, valid, isUploadDocument }) => {
    return {
      ...state,
      international: {
        ...state.international,
        commercialInvoice: {
          ...state.international.commercialInvoice,
          value,
          valid,
          isUploadDocument,
        },
      },
    };
  }),
  on(ClearShipOrQuote, () => {
    return initialShipOrQuoteState;
  }),
  on(ClearOptionsStep, (state) => {
    return {
      ...state,
      options: {
        ...initialShipOrQuoteState.options,
        labelSize: {
          ...state.options.labelSize,
        },
        notificationSettings: state.options.notificationSettings
          ? { ...state.options.notificationSettings }
          : { value: [], valid: true },
      },
    };
  }),
  on(ClearInternationalStep, (state) => {
    return {
      ...state,
      international: {
        ...initialShipOrQuoteState.international,
      },
    };
  }),

  on(SetShipmentEEIShowComponent, (state, { show }) => {
    return {
      ...state,
      international: {
        ...state.international,
        eei: {
          ...state.international.eei,
          show,
        },
      },
    };
  }),
  on(SetShipmentEEI, (state, { require }) => {
    return {
      ...state,
      international: {
        ...state.international,
        eei: {
          ...state.international.eei,
          value: {
            require,
          },
          valid: require !== null,
        },
      },
    };
  }),
  on(SetPackages, (state, { packages }) => {
    return {
      ...state,
      packages,
    };
  }),
  on(SetServiceOptions, (state, { serviceOptions }) => {
    return {
      ...state,
      serviceOptions,
    };
  }),
  on(SetServiceAndPackageAvailabilityLoading, (state, { loading }) => {
    return {
      ...state,
      serviceAndPackageAvailabilityLoading: loading,
    };
  }),
  on(SetServiceType, (state, { serviceType }) => {
    return {
      ...state,
      serviceType: serviceType,
    };
  }),
  on(SetAdditionalDocuments, (state, { documents }) => {
    return {
      ...state,
      options: {
        ...state.options,
        additionalDocuments: {
          documents,
          valid: true,
        },
      },
    };
  })
);
